import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

// Toast Context
const ToastContext = createContext(null);

// Toast types with their icons and colors
const toastConfig = {
  success: {
    icon: CheckCircle,
    bgLight: 'bg-green-50 border-green-200',
    bgDark: 'bg-green-900/30 border-green-800',
    iconColor: 'text-green-500',
    textLight: 'text-green-800',
    textDark: 'text-green-200'
  },
  error: {
    icon: XCircle,
    bgLight: 'bg-red-50 border-red-200',
    bgDark: 'bg-red-900/30 border-red-800',
    iconColor: 'text-red-500',
    textLight: 'text-red-800',
    textDark: 'text-red-200'
  },
  warning: {
    icon: AlertCircle,
    bgLight: 'bg-yellow-50 border-yellow-200',
    bgDark: 'bg-yellow-900/30 border-yellow-800',
    iconColor: 'text-yellow-500',
    textLight: 'text-yellow-800',
    textDark: 'text-yellow-200'
  },
  info: {
    icon: Info,
    bgLight: 'bg-blue-50 border-blue-200',
    bgDark: 'bg-blue-900/30 border-blue-800',
    iconColor: 'text-blue-500',
    textLight: 'text-blue-800',
    textDark: 'text-blue-200'
  }
};

// Individual Toast Component
const Toast = ({ toast, onDismiss, darkMode }) => {
  const config = toastConfig[toast.type] || toastConfig.info;
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg max-w-sm w-full animate-slide-in ${
        darkMode ? config.bgDark : config.bgLight
      }`}
      role="alert"
    >
      <Icon className={`${config.iconColor} flex-shrink-0 mt-0.5`} size={20} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className={`font-semibold text-sm ${darkMode ? config.textDark : config.textLight}`}>
            {toast.title}
          </p>
        )}
        <p className={`text-sm ${darkMode ? config.textDark : config.textLight} ${toast.title ? 'mt-1' : ''}`}>
          {toast.message}
        </p>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className={`flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors ${
          darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, onDismiss, darkMode }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
          darkMode={darkMode}
        />
      ))}
    </div>
  );
};

// Toast Provider Component
export const ToastProvider = ({ children, darkMode = false }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message, title = '', duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, type, message, title };

    setToasts(prev => [...prev, newToast]);

    // Auto dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (message, title = '') => addToast('success', message, title),
    error: (message, title = '') => addToast('error', message, title),
    warning: (message, title = '') => addToast('warning', message, title),
    info: (message, title = '') => addToast('info', message, title),
    dismiss: dismissToast,
    dismissAll: () => setToasts([])
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} darkMode={darkMode} />
    </ToastContext.Provider>
  );
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default Toast;
